package com.steamstreet.vegasful.html.components

import com.steamstreet.vegasful.css.css
import kotlinx.css.fontSize
import kotlinx.css.px
import kotlinx.html.FlowContent
import kotlinx.html.i
import kotlinx.html.span
import kotlinx.html.unsafe

enum class IconType(val prefix: String) {
    Solid("fas"),
    Regular("far"),
    Light("far"),
    Duotone("fad"),
    Brand("fab")
}

enum class SpinType(val type: String) {
    Spin("spin"),
    Pulse("pulse"),
    None("")
}

fun FlowContent.icon(id: String, type: IconType = IconType.Solid, spin: SpinType = SpinType.None) {
    i("${type.prefix} fa-$id ${if (spin != SpinType.None) "fa-${spin.type}" else ""}") {}
}

/** Marks the spinner's wrapper, so a caller can show and hide it without reaching for a tag name. */
const val SPINNER_CLASS = "vegasful-spinner"

/**
 * A spinning ring, sized to the text it sits in and coloured by it.
 *
 * The markup is shared with the client-side registry in `web/public`, so there is one spinner in
 * the product rather than one per rendering path.
 *
 * **Animated with SMIL rather than CSS keyframes**, following the choice already made when Font
 * Awesome was removed: the SVG carries its own animation, so it needs no companion stylesheet and
 * works wherever the markup lands. Keyframes would also have had nowhere to live — the `css {}`
 * builder cannot express them, since `CssManager.writeStyleSheet` treats every `@` rule as a media
 * query and nests the element's selector inside it.
 */
const val SPINNER_SVG: String =
    """<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" """ +
            """stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="1em" height="1em" """ +
            """aria-hidden="true" focusable="false" style="display:inline-block;vertical-align:-0.125em">""" +
            """<path d="M21 12a9 9 0 1 1-6.219-8.56">""" +
            """<animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" """ +
            """dur="0.8s" repeatCount="indefinite"/>""" +
            """</path></svg>"""

/**
 * Render the spinner.
 *
 * This was `icon("spinner", spin = SpinType.Spin)`, which emits Font Awesome's classes — and Font
 * Awesome was removed from the site over licensing. It rendered an empty `<i>`, so every spinner
 * built on it was invisible and silent about it, including the one My Guide shows while loading.
 *
 * Sized to `1em` and painted with `currentColor`, so surrounding `font-size` and `color` control it
 * exactly as the glyph did — which is what lets [largeSpinner] keep working by setting a font size
 * and nothing else.
 *
 * **It does not hide itself.** A spinner is shown for part of a component's life and not the rest,
 * and only the component knows when, so the caller owns its visibility.
 */
fun FlowContent.spinner() {
    span(classes = SPINNER_CLASS) {
        unsafe { +SPINNER_SVG }
    }
}

fun FlowContent.largeSpinner() {
    span {
        css {
            fontSize = 36.px
        }
        spinner()
    }
}